import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  PlusIcon,
  Eye,
  Edit,
  Camera,
  BookOpen,
  Smartphone,
  Search,
  FilterX,
} from "lucide-react";

import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import {
  FieldTooltip,
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn, TableAction } from "@shared/types/table";
import { useGetAllRoutes, useRoutesExport, useRouteSummary } from "../../hooks";
import { PAGE_SIZE } from "@shared/utils/constants";
import {
  useGetArea,
  useGetMeterType,
} from "@features/mx/readCycleManagement/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { KpiCards } from "./kpiCards";

const CreateRouteButton = () => {
  const navigate = useNavigate();

  const handleCreateRoute = () => {
    navigate("/mx/routeManagement/add");
  };

  return (
    <Button onClick={handleCreateRoute}>
      <PlusIcon className="h-4 w-4 mr-2" />
      Create Route
    </Button>
  );
};

interface ReadCycleData {
  id: string;
  meterCycleName: string;
  readType: "Manual" | "Photo" | "Smart";
  routes: number;
  meters: number;
  consumers: number;
  completionPercentage: number;
  createdOn: string;
  createdBy: string;
  runCount: number;
  nextRunDate: string;
  status: "Scheduled" | "In Progress" | "Completed" | "Delayed";
  routeName: string[];
  summary: {
    premiseCount: number;
    areaCount: number;
    subAreaCount: number;
    totalMeterCount: number;
  };
}

const RoutesLanding: React.FC = () => {
  const navigate = useNavigate();
  const utilityId = useRemoteUtilityId();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: meterTypeData } = useGetMeterType();
  const { data: areaData } = useGetArea();

  // URL Params — single source of truth
  const currentPage = Number(searchParams.get("page") || "1");
  const currentSearch = decodeURIComponent(searchParams.get("search") || "");
  const currentReadType = searchParams.get("readType") || "all";
  const currentArea = searchParams.get("area") || "all";

  // UI State for pending edits
  const [pendingFilters, setPendingFilters] = useState({
    readType: decodeURIComponent(currentReadType),
    area: decodeURIComponent(currentArea),
  });

  // Sync pendingFilters when URL changes
  useEffect(() => {
    setPendingFilters({
      readType: decodeURIComponent(currentReadType),
      area: decodeURIComponent(currentArea),
    });
  }, [currentReadType, currentArea]);

  const queryParams = useMemo(
    () => ({
      page: currentPage,
      searchData: currentSearch,
      readType: currentReadType,
      area: currentArea,
    }),
    [currentPage, currentSearch, currentReadType, currentArea]
  );

  const { data: routesData } = useGetAllRoutes(
    queryParams.page,
    queryParams.searchData,
    queryParams.readType,
    queryParams.area
  );

  const { exportCSV, isExporting } = useRoutesExport(
    utilityId,
    queryParams.searchData,
    queryParams.readType,
    queryParams.area
  );

  const handlePendingFilterChange = (
    key: keyof typeof pendingFilters,
    value: string
  ) => {
    setPendingFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleApplyFilters = () => {
    console.log("pendingFilters", pendingFilters);
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      ...(pendingFilters.readType && { readType: pendingFilters.readType }),
      ...(pendingFilters.area && { area: pendingFilters.area }),
      page: 1,
    };

    setSearchParams(newParams);
  };

  const handleResetFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    delete newParams.readType;
    delete newParams.area;
    delete newParams.search;
    newParams.page = 1;

    setSearchParams(newParams);
  };

  const handleSearch = (searchTerm: string) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      ...(currentReadType && { readType: currentReadType }),
      ...(currentArea && { area: currentArea }),
    };
    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search;
    }
    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };

    setSearchParams(newParams);
  };

  const handleExport = () => {
    exportCSV();
  };

  // Define table columns
  const columns: TableColumn<ReadCycleData>[] = [
    {
      key: "routeName",
      header: "Route Name",
      sortable: true,
    },
    {
      key: "meterTypeDisplay",
      header: "Read Type",
      sortable: true,
      render: (value: string) => (
        <div className="flex items-center gap-2">
          {value === "Manual Meter" && (
            <BookOpen className="h-4 w-4 text-orange-500" />
          )}
          {value === "Photo Meter" && (
            <Camera className="h-4 w-4 text-blue-500" />
          )}
          {value === "Smart Meter" && (
            <Smartphone className="h-4 w-4 text-purple-500" />
          )}
          <span>{value}</span>
        </div>
      ),
    },
    {
      key: "premiseCount",
      header: "Premises",
      sortable: true,
      width: "w-20",
    },
    {
      key: "meterCount",
      header: "Meters",
      sortable: true,
      width: "w-20",
    },
    {
      key: "consumerCount",
      header: "Consumers",
      sortable: true,
      width: "w-24",
    },
    {
      key: "createdDate",
      header: "Created On",
      sortable: true,
      responsive: { hidden: "md:table-cell" },
    },
    {
      key: "createdUserRemoteName",
      header: "Created By",
      sortable: true,
      responsive: { hidden: "lg:table-cell" },
    },
  ];

  const actions: TableAction[] = [
    {
      label: "View",
      icon: "Eye",
      onClick: (row: ReadCycleData) => {
        navigate(
          `/mx/routeManagement/view/view-details/${row.id}/${row.routeName}`
        );
      },
    },
    {
      label: "Edit",
      icon: <Edit className="h-4 w-4" />,
      onClick: (row: ReadCycleData) => {
        navigate(`/mx/routeManagement/edit/${row.id}`);
      },
    },
  ];

  const pagination = routesData
    ? {
        currentPage,
        totalPages: Math.ceil(routesData.count / PAGE_SIZE),
        totalItems: routesData.count,
        startItem: (currentPage - 1) * PAGE_SIZE + 1,
        endItem: Math.min(currentPage * PAGE_SIZE, routesData.count),
        itemsPerPage: PAGE_SIZE,
      }
    : undefined;

  const FilterPanel = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Read Type</label>
          <Select
            value={pendingFilters.readType}
            onValueChange={(value) =>
              handlePendingFilterChange("readType", value)
            }
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select read type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {meterTypeData?.map((type) => (
                <SelectItem key={type.key} value={String(type.key)}>
                  {type.value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Area</label>
          <Select
            value={pendingFilters.area}
            onValueChange={(value) => handlePendingFilterChange("area", value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select area" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Areas</SelectItem>
              {areaData?.map((area) => (
                <SelectItem key={area.code} value={String(area.code)}>
                  {area.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <Button variant="default" onClick={handleApplyFilters}>
          Apply Filters
        </Button>
        <Button variant="ghost" onClick={handleResetFilters}>
          <FilterX className="w-4 h-4 mr-1" /> Clear
        </Button>
      </div>
    </div>
  );

  const hasActiveFilters = currentReadType !== "all" || currentArea !== "all";

  return (
    <div className="space-y-6 p-1">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="page-title">Routes Management</h2>
          <p className="page-subtitle">
            Manage service route configurations and optimize route planning.
          </p>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <CreateRouteButton />
            </TooltipTrigger>
            <FieldTooltip>
              Create a new read cycle to schedule when and how meters will be
              read
            </FieldTooltip>
          </Tooltip>
        </TooltipProvider>
      </div>
            <KpiCards />

      <div className="mt-2">
  
        <GenericTable
          data={routesData?.results ?? []}
          columns={columns}
          rowKey="id"
          actions={actions}
          actionsType="icons"
          pagination={pagination}
          onPageChange={handlePageChange}
          onSearch={handleSearch}
          searchPlaceholder="Search routes by name..."
          searchDebounceMs={500}
          filterPanel={FilterPanel}
          onExport={handleExport}
          exportLoading={isExporting}
          emptyMessage="No routes found"
          emptyIcon={<Search className="h-8 w-8 text-muted-foreground mb-2" />}
          getRowClassName={(row) =>
            row.status === "Delayed"
              ? "bg-red-50"
              : row.status === "In Progress"
              ? "bg-blue-50"
              : ""
          }
          filters={
            hasActiveFilters ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Filters active
                </span>
              </div>
            ) : null
          }
        />
      </div>
    </div>
  );
};

export default RoutesLanding;
